// Pages/Home/Home.jsx
import React, { useState, useEffect } from "react";
import { getBlogList } from "../../Services/BlogService";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";
import Loader from "../../Components/Core/Loader";
import ArticleCard from "./ArticleCard";
import InfoModal from "../../Components/Modals/InfoModal";
import Footer from "../../Components/Core/InfoFooter";
import HomeHeroSection from "./HomeHeroSection";
import ServiceCards from "./ServiceCards";
import CommentHome from "./CommentHome";
import PortfolioHome from "./PortfolioHome";
import AboutHome from "./AboutHome";
import { Link } from "react-router-dom";
import SeoHead from "../../Components/Core/SeoHead";
import { getHomeInfo } from "../../Services/CommonService";
import { useTenantContent } from "../../Context/TenantContentContext";
import {
  buildPhysicianJsonLd,
  doctorDisplayName,
  joinUrl,
} from "../../utils/seo";

const Home = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [homeInfo, setHomeInfo] = useState();
  const [openInfoModal, setOpenInfoModal] = useState(false);

  const { content, loading: contentLoading } = useTenantContent();

  useEffect(() => {
    if (!content || !content.showHomeInitialModal) return;

    const lastShown = localStorage.getItem("HomeInitialModalLastShown");
    const now = Date.now();
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

    if (!lastShown || now - parseInt(lastShown, 10) > oneWeekInMs) {
      setOpenInfoModal(true);
    }
  }, [content]);

  const handleInfoModalClose = () => {
    setOpenInfoModal(false);
    localStorage.setItem("HomeInitialModalLastShown", Date.now().toString());
  };

  const fetchData = async () => {
    try {
      const response = await getBlogList();
      setBlogs(response);
      const homeInfo = await getHomeInfo();
      setHomeInfo(homeInfo);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || contentLoading || !content) {
    return <Loader />;
  }

  const pageTitle = `${content.defaultPageTitle} | ${content.siteName}`;
  const pageUrl = joinUrl(content.canonicalUrl, "");
  const doctorName = doctorDisplayName(content);
  const ogImage =
    content.ogImage || content.doctorImageUrl || content.doctorAvatarUrl;

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={content.siteDescription}
        keywords={content.siteKeywords}
        canonical={pageUrl}
        image={ogImage}
        imageAlt={doctorName}
        siteName={content.siteName}
        jsonLd={buildPhysicianJsonLd(content, pageUrl)}
      />

      {/* Conditional Initial Modal */}
      {content.showHomeInitialModal && (
        <InfoModal
          openDialog={openInfoModal}
          handleCloseDialog={handleInfoModalClose}
          title={content.homeInitialModalTitle || "اطلاعیه"}
          text={content.homeInitialModalContent || []}
          confirnButtonText={"متوجه شدم"}
        />
      )}

      {/* Conditional Hero Section */}
      {content.showHeroSection && (
        <HomeHeroSection
          ConsultationCount={homeInfo?.totalConsultationsCount}
          DoctorName={`دکتر ${content.doctorName}`}
          DoctorSpecialty={content.doctorSpecialty}
          PatientCount={content.patientCount}
          ShowPatientCount={content.showPatientCount}
          ShowYearExprience={content.showYearExprience}
          ShowOnlineCounselingCount={content.showOnlineCounselingCount}
          YearExprience={content.yearExprience}
          HeroTitle={content.heroTitle}
          HeroImage={content.doctorImageUrl}
          HeroSubtitle={content.heroSubtitle}
          isReservationPageEnabled={content.isReservationPageEnabled}
          isConsultationPageEnabled={content.isConsultationPageEnabled}
          isBlogPageEnabled={content.isBlogPageEnabled}
        />
      )}

      <div className="container mb-4">
        {/* Conditional About Section */}
        {content.showAboutSection && (
          <>
            <div className="row mt-5 justify-content-center mb-3">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="mb-0">
                  <Link
                    to="/about"
                    className="home-topic iran-sans"
                  >
                    درباره پزشک
                    <hr className="border-success border-2 w-75 mb-0 mt-1" />
                  </Link>
                </h2>
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="col-11">
                <AboutHome content={content} />
              </div>
            </div>
          </>
        )}

        {/* Conditional Blog Section */}
        {content.showBlogSection && content.isBlogPageEnabled !== false && blogs.length > 0 && (
          <>
            <div className="row mt-5 justify-content-center mb-3">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="mb-0">
                  <Link
                    to="/blog/index"
                    className="home-topic iran-sans"
                  >
                    آخرین مقالات
                    <hr className="border-success border-2 w-75 mb-0 mt-1" />
                  </Link>
                </h2>
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-11">
                <div className="home-articles-grid">
                  {blogs.slice(0, 6).map((itm) => (
                    <ArticleCard
                      Image={itm.imageUrl}
                      Date={itm.dateDisplay}
                      Author={itm.authorName}
                      Title={itm.title}
                      ViewCount={itm.viewCount}
                      key={itm.id}
                      id={itm.id}
                      slug={itm.slug}
                    />
                  ))}
                </div>
                <div className="text-center mt-4">
                  <Link to="/blog/index" className="btn-clear fw-bold rounded-5 iran-sans">مشاهده بیشتر</Link>
                </div>
              </div>
            </div>
          </>
        )}

        {/*  Conditional Reservation Section */}
        {content.showReservationSection && content.isReservationPageEnabled !== false && (
          <>
            <div className="row mt-5 justify-content-center mb-3">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="mb-0">
                  <Link
                    to="/reservation/reserve"
                    className="home-topic iran-sans"
                  >
                    رزرو نوبت
                    <hr className="border-success border-2 w-75 mb-0 mt-1" />
                  </Link>
                </h2>
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-11 justify-content-start">
                <Link
                  to={"/reservation/reserve"}
                  className="color-green pe-4 iran-sans fw-bold"
                  style={{ fontSize: "1.3rem", textDecoration: "none" }}
                >
                  اولین نوبت آزاد بعدی:{" "}
                  <span className="pe-2 color-green opacity-75 color-blue iran-sans">
                    {homeInfo?.firstAwailibaleDayName || "در حال بارگذاری..."}
                  </span>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Conditional Services Section */}
        {content.showServicesSection && (
          <>
            <div className="row mt-5 justify-content-center mb-2">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="home-topic iran-sans mb-0">
                  خدمات
                  <hr className="border-success border-2 w-75 mb-0 mt-1" />
                </h2>
              </div>
            </div>

            <div className="row justify-content-center d-flex">
              <div className="col-11">
                <ServiceCards />
              </div>
            </div>
          </>
        )}

        {content.showPortfolioSection && (
          <>
            <div className="row mt-5 justify-content-center mb-2">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="home-topic iran-sans mb-0">
                  نمونه کارها
                  <hr className="border-success border-2 w-75 mb-0 mt-1" />
                </h2>
              </div>
            </div>

            <div className="row justify-content-center d-flex">
              <div className="col-11">
                <PortfolioHome />
              </div>
            </div>
          </>
        )}

        {/* Conditional Comments Section */}
        {content.showCommentsSection && (
          <>
            <div className="row mt-5 justify-content-center mb-2">
              <div className="col-11 justify-content-start font-semi-big fw-light">
                <h2 className="home-topic iran-sans mb-0">
                  نظرات بیماران
                  <hr className="border-success border-2 w-75 mb-0 mt-1" />
                </h2>
              </div>
            </div>

            <div className="row justify-content-center d-flex">
              <div className="col-11">
                <CommentHome />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ✅ Conditional Footer */}
      {content.showFooter && <Footer />}
    </>
  );
};

export default Home;
